import { Injectable } from '@angular/core';
import { Attachment } from '@jms/model';
import { ArrayUtility } from '@jms/utils/util.redux';
import { Action, Store } from '@ngrx/store';
import * as Enumerable from 'linq';
import { Observable } from 'rxjs';
export type FileUPloadStatus = 'pending' | 'uploading' | 'success' | 'failed';

export interface FileInfo {
  id: number;
  name: string;
  status?: FileUPloadStatus;
  progress?: number;
  attachment?: Attachment;
  entity?: any;
  metadata?: any;
}

export interface UploaderState {
  showDialog: boolean;
  isUploading: boolean;
  files: FileInfo[];
  totalProgress: number;
  uploadServiceAlready: boolean;
  uploadedFiles: FileInfo[];
}

export const uploaderInitialState: UploaderState = {
  showDialog: false,
  isUploading: false,
  totalProgress: 0,
  files: [],
  uploadServiceAlready: false,
  uploadedFiles: []
};


@Injectable({ providedIn: 'root' })
export class UploaderActions {
  public state$: Observable<UploaderState>;
  public showDialog$: Observable<boolean>;
  public isUploading$: Observable<boolean>;
  public totalProgress$: Observable<number>;
  public files$: Observable<FileInfo[]>;
  public uploadServiceAlready$: Observable<boolean>;
  public uploadedFiles$: Observable<FileInfo[]>;

  constructor(public store: Store<any>) {
    this.state$ = store.select(x => x.uploader);
    this.showDialog$ = store.select(x => x.uploader.showDialog);
    this.isUploading$ = store.select(x => x.uploader.isUploading);
    this.totalProgress$ = store.select(x => x.uploader.totalProgress);
    this.files$ = store.select(x => x.uploader.files);
    this.uploadServiceAlready$ = store.select(x => x.uploader.uploadServiceAlready);
    this.uploadedFiles$ = store.select(state => state.uploader.uploadedFiles);
  }

  static SHOW_DETAILS = '[Uploader] ShowDetails';
  showUploadDetails(isDetailsVisible: boolean, dispatch = true) {
    return this.invokeAction(UploaderActions.SHOW_DETAILS, isDetailsVisible, dispatch);
  }

  static ADD_FILE = '[Uploader] AddFile';
  addFile(id: number, name: string, metadata: string = null, dispatch = true) {
    const data = {
      id: id,
      name: name,
      status: 'pending',
      metadata: metadata
    } as FileInfo;
    return this.invokeAction(UploaderActions.ADD_FILE, data, dispatch);
  }

  static BEGIN_UPLOAD = '[Uploader] BeginUpload';
  beginUpload(id: number, name: string, dispatch = true) {
    const data = {
      id: id,
      name: name,
      status: 'uploading'
    } as FileInfo;
    return this.invokeAction(UploaderActions.BEGIN_UPLOAD, data, dispatch);
  }

  static UPLOAD_COMPLETED = '[Uploader] UploadCompleted';
  uploadCompleted(id: number, name: string, status: FileUPloadStatus, uploadReponse: any, dispatch = true) {
    const data = {
      id: id,
      name: name,
      status: status,
      attachment: uploadReponse ? uploadReponse.attachment as Attachment : null,
      entity: uploadReponse ? uploadReponse.data : null
    } as FileInfo;
    return this.invokeAction(UploaderActions.UPLOAD_COMPLETED, data, dispatch);
  }

  static UPLOAD_ALL_COMPLETED = '[Uploader] UploadAllCompleted';
  uploadAllCompleted(dispatch = true) {
    return this.invokeAction(UploaderActions.UPLOAD_ALL_COMPLETED, null, dispatch);
  }

  static UPLOAD_ON_PROGRESS = '[Uploader] OnProgress';
  onProgress(id: number, name: string, uploadedBytes: number, totalBytes: number, dispatch = true) {
    const data = {
      id: id,
      name: name,
      uploadedBytes: uploadedBytes,
      totalBytes: totalBytes
    };
    return this.invokeAction(UploaderActions.UPLOAD_ON_PROGRESS, data, dispatch);
  }

  static UPLOAD_ON_TOTAL_PROGRESS = '[Uploader] On Total Progress';
  onTotalProgress(progress: number, dispatch = true) {
    return this.invokeAction(UploaderActions.UPLOAD_ON_TOTAL_PROGRESS, progress, dispatch);
  }

  static UPLOAD_CLEAR_ALL = '[Uploader] ClearALL';
  clearAll(dispatch = true) {
    return this.invokeAction(UploaderActions.UPLOAD_CLEAR_ALL, null, dispatch);
  }

  static UPLOAD_SERVICE_ALREADY = '[Uploader] Service already';
  initServiceAlready(dispatch = true) {
    return this.invokeAction(UploaderActions.UPLOAD_SERVICE_ALREADY, null, dispatch);
  }

  invokeAction(type: string, data: any, dispatch = true) {
    const action = {
      type: type,
      payload: data
    };
    if (dispatch) this.dispatch(action);

    return action;
  }

  dispatch(action: Action) {
    this.store.dispatch(action);
  }
}

export function uploaderReducer(state: UploaderState = uploaderInitialState, action: Action & { payload?: any }) {
  switch (action.type) {
    case UploaderActions.UPLOAD_ON_TOTAL_PROGRESS:
      return { ...state, totalProgress: action.payload };

    case UploaderActions.ADD_FILE: {
      const items = [...state.files, action.payload];
      return { ...state, files: items };
    }

    case UploaderActions.SHOW_DETAILS:
      return { ...state, showDialog: action.payload };

    case UploaderActions.BEGIN_UPLOAD: {
      const file = action.payload as FileInfo;
      const index = Enumerable.from(state.files).indexOf(x => x.id === file.id);
      const files = ArrayUtility.replace(state.files, { ...state.files[index], ...file }, index);
      return { ...state, isUploading: true, files: files };
    }

    case UploaderActions.UPLOAD_COMPLETED: {
      const filePayload = action.payload;

      const index = Enumerable.from(state.files).indexOf(x => x.id === filePayload.id);
      let files = [];
      const uploadedFiles = [...state.uploadedFiles];
      if (filePayload.status === 'success') {
        files = [...state.files];
        files.splice(index, 1);
        uploadedFiles.push(filePayload);
      } else {
        const newFile = {
          ...state.files[index],
          status: filePayload.status,
          attachment: filePayload.attachment
        } as FileInfo;
        files = ArrayUtility.replace(state.files, newFile, index);
      }
      return { ...state, files: files, uploadedFiles: [...uploadedFiles] };
    }

    case UploaderActions.UPLOAD_ALL_COMPLETED:
      return { ...state, isUploading: false, totalProgress: 0, uploadedFiles: [] };

    case UploaderActions.UPLOAD_ON_PROGRESS: {
      const file = action.payload;
      const index = Enumerable.from(state.files).indexOf(x => x.id === file.id);
      const newFile = { ...state.files[index], progress: Math.floor(file.uploadedBytes / file.totalBytes * 100) };
      const files = ArrayUtility.replace(state.files, newFile, index);
      return { ...state, files: files };
    }
    case UploaderActions.UPLOAD_CLEAR_ALL: {
      return { ...state, files: [], uploadedFiles: [] };
    }
    case UploaderActions.UPLOAD_SERVICE_ALREADY: {
      return { ...state, uploadServiceAlready: true };
    }
  }
  return state;
}
